import React, {useState, useEffect} from 'react';
import {fromJS} from 'immutable';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import classnames from 'classnames';
import {history} from 'client/history';
import {Text, TabNavigation} from '@albert-io/atomic';

import sessionStore from 'client/Session/SessionStore';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {refreshSession} from '@albert-io/mandark/authentication';

import LoadingIndicator from 'client/generic/LoadingIndicator.react';

import './account-settings.scss';

const getUserQuery = () => {
  return resource('users_v2')
    .mandarkEndpoint(['users_v2', sessionStore.getUserId()])
    .include('school_v5')
    .include('school_v5.district_v1')
    .include('teacher_v1.classrooms_v1')
    .include('student_v2.classrooms_v1')
    .include('standard_set_creators_v1')
    .filter({
      included: {
        'student_v2.classrooms_v1': {
          status: 'active'
        },
        'teacher_v1.classrooms_v1': {
          status: 'active'
        }
      }
    });
};

const AccountSettings = ({children, location}) => {
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let subscribed = true;
    async function fetchUser() {
      try {
        const res = await getUserQuery().getResourcePromise();
        if (subscribed) {
          setUser(res);
          setIsReady(true);
          refreshSession();
        }
      } catch (error) {
        history.pushState(null, '/error');
      }
    }

    fetchUser();
    return () => {
      subscribed = false;
    };
  }, [isReady]);

  if (!user) {
    return <LoadingIndicator />;
  }

  const sidebarLinks = fromJS([
    {to: '/account/profile', label: 'Profile'},
    {to: '/account/access', label: user.isTeacher() ? 'Licenses & Subscriptions' : 'Subscriptions'},
    {to: '/account/settings', label: 'Password & Settings'}
  ]);

  return (
    <div className='account-settings__container u-mar-lr_auto u-mar-tb_0 u-width_100pc'>
      <div
        className={classnames('account-settings__sidebar', {
          'u-display_none': location.pathname === '/account/deactivate'
        })}
      >
        <div className='u-display_flex u-align-items_center u-justify-content_flex-start u-pad-t_0 u-pad-b_0 u-mar-b_8 u-pad-lr_3'>
          <div className='account-settings__user-avatar u-mar-r_2 u-pad_1 u-font-weight_bold u-align-items_center u-display_flex u-justify-content_center'>
            <Text
              size='m'
              className='account-settings__user-avatar-text'
              aria-label={user.getFullName()}
              role='img'
            >
              {user.getFirstName().charAt(0).toUpperCase()}
              {user.getLastName().charAt(0).toUpperCase()}
            </Text>
          </div>
          <div>
            <div>
              <Text size='regular' bold emphasis='high'>
                {user.getDisplayName()}
              </Text>
            </div>
            <div>
              <Text size='m' emphasis='lowest'>
                {user.isTeacher() ? 'Teacher' : 'Student'}
              </Text>
            </div>
          </div>
        </div>
        <TabNavigation
          align='left'
          paddingLevel={0}
          as='nav'
          className='account-settings__sidebar-links--tabs u-list-style_none u-pad_0 u-width_100pc'
          aria-label='Settings categories'
        >
          {sidebarLinks.map((link) => (
            <TabNavigation.Tab
              key={link.get('to')}
              as={Link}
              to={link.get('to')}
              isActive={location.pathname === link.get('to')}
            >
              {link.get('label')}
            </TabNavigation.Tab>
          ))}
        </TabNavigation>
        <nav>
          <ul className='account-settings__sidebar-links u-display_flex u-list-style_none'>
            {sidebarLinks.map((link) => (
              <li
                key={link.get('to')}
                className={classnames(
                  'account-settings__sidebar-links__sidebar-item u-mar-b_2 u-display_flex u-bgc_transparent',
                  {
                    'account-settings__sidebar-links__sidebar-item--active':
                      location.pathname === link.get('to')
                  }
                )}
              >
                <Link
                  className='u-pad-lr_3 u-pad-tb_2 u-white-space_nowrap u-text-decoration_none account-settings__sidebar-links__sidebar-item__a'
                  to={link.get('to')}
                >
                  {link.get('label')}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      <div className='account-settings__content u-pad-tb_0 u-pad-lr_3'>
        {React.cloneElement(children, {user, setIsReady})}
      </div>
    </div>
  );
};

AccountSettings.propTypes = {
  children: PropTypes.node,
  location: PropTypes.object
};

export default AccountSettings;
